import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './AnonymousLoginModal.css';

interface AnonymousLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  downloadType: 'gcode' | 'file';
  fileName?: string;
}

export function AnonymousLoginModal({ 
  isOpen, 
  onClose, 
  downloadType,
  fileName 
}: AnonymousLoginModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // DISABLE ALL HOVER EFFECTS ON PAGE WHEN MODAL IS OPEN
  React.useEffect(() => {
    if (isOpen) {
      // Store original styles to restore later
      const originalStyles = new Map();
      
      // 1. ADD GLOBAL CLASS TO DISABLE ALL HOVER TRANSFORMS
      document.body.classList.add('modal-open-no-hover');
      
      // 2. HIDE ALL CANVAS ELEMENTS COMPLETELY
      const canvases = document.querySelectorAll('canvas');
      canvases.forEach(canvas => {
        originalStyles.set(canvas, {
          display: canvas.style.display,
          visibility: canvas.style.visibility,
          pointerEvents: canvas.style.pointerEvents
        });
        canvas.style.display = 'none';
        canvas.style.visibility = 'hidden';
        canvas.style.pointerEvents = 'none';
      });

      // 3. PAUSE ALL CSS ANIMATIONS GLOBALLY
      const style = document.createElement('style');
      style.id = 'modal-animation-freeze';
      style.textContent = `
        /* DISABLE ALL HOVER TRANSFORMS WHEN MODAL IS OPEN */
        body.modal-open-no-hover *:hover {
          transform: none !important;
          transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease !important;
        }
        
        /* SPECIFICALLY TARGET PROBLEMATIC HOVER EFFECTS */
        body.modal-open-no-hover .gcode-folder-card:hover,
        body.modal-open-no-hover .gcode-card:hover,
        body.modal-open-no-hover .gcode-preview-card:hover,
        body.modal-open-no-hover .model-card:hover,
        body.modal-open-no-hover .download-all-button:hover,
        body.modal-open-no-hover .expand-button:hover,
        body.modal-open-no-hover .delete-button:hover,
        body.modal-open-no-hover .button:hover,
        body.modal-open-no-hover .printer-card:hover {
          transform: none !important;
        }
        
        /* PAUSE ALL OTHER ANIMATIONS */
        body.modal-open-no-hover *, 
        body.modal-open-no-hover *::before, 
        body.modal-open-no-hover *::after {
          animation-play-state: paused !important;
          animation-duration: 0s !important;
          transition-duration: 0s !important;
        }
        
        /* SPECIFICALLY TARGET MODAL TO KEEP IT FUNCTIONAL */
        body.modal-open-no-hover .simple-modal-overlay,
        body.modal-open-no-hover .simple-modal-overlay *,
        body.modal-open-no-hover .simple-modal,
        body.modal-open-no-hover .simple-modal * {
          animation-play-state: running !important;
          animation-duration: initial !important;
          transition-duration: initial !important;
        }
        
        /* ALLOW SAFE TRANSITIONS ON MODAL ELEMENTS */
        body.modal-open-no-hover .simple-close-btn,
        body.modal-open-no-hover .simple-primary-btn,
        body.modal-open-no-hover .simple-secondary-btn {
          transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease !important;
        }
      `;
      document.head.appendChild(style);

      // 4. DISABLE BODY SCROLL AND INTERACTION
      const originalBodyOverflow = document.body.style.overflow;
      const originalBodyPointerEvents = document.body.style.pointerEvents;
      document.body.style.overflow = 'hidden';
      
      // 5. STOP ALL REQUESTANIMATIONFRAME LOOPS (if any)
      let animationIds: number[] = [];
      const originalRequestAnimationFrame = window.requestAnimationFrame;
      window.requestAnimationFrame = (callback) => {
        // Don't start new animation frames while modal is open
        return 0;
      };

      // 6. TRY TO DISABLE THREE.JS RENDERER LOOPS
      try {
        // Look for Three.js renderers and pause them
        canvases.forEach(canvas => {
          if ((canvas as any).__r3f) {
            const state = (canvas as any).__r3f;
            if (state.gl && state.gl.setAnimationLoop) {
              state.gl.setAnimationLoop(null);
            }
          }
        });
      } catch (error) {
        console.log('Could not pause Three.js renderers:', error);
      }

      // Cleanup function
      return () => {
        // REMOVE GLOBAL NO-HOVER CLASS
        document.body.classList.remove('modal-open-no-hover');
        
        // Restore canvas visibility
        canvases.forEach(canvas => {
          const original = originalStyles.get(canvas);
          if (original) {
            canvas.style.display = original.display;
            canvas.style.visibility = original.visibility;
            canvas.style.pointerEvents = original.pointerEvents;
          }
        });

        // Remove animation freeze styles
        const freezeStyle = document.getElementById('modal-animation-freeze');
        if (freezeStyle) {
          freezeStyle.remove();
        }

        // Restore body styles
        document.body.style.overflow = originalBodyOverflow;
        document.body.style.pointerEvents = originalBodyPointerEvents;

        // Restore requestAnimationFrame
        window.requestAnimationFrame = originalRequestAnimationFrame;

        // Try to restart Three.js renderers
        try {
          canvases.forEach(canvas => {
            if ((canvas as any).__r3f) {
              const state = (canvas as any).__r3f;
              if (state.gl && state.gl.setAnimationLoop && state.advance) {
                state.gl.setAnimationLoop(state.advance);
              }
            }
          });
        } catch (error) {
          console.log('Could not restart Three.js renderers:', error);
        }

        // Force a page refresh if needed (last resort)
        setTimeout(() => {
          const allCanvases = document.querySelectorAll('canvas');
          allCanvases.forEach(canvas => {
            if ((canvas as any).__r3f) {
              try {
                (canvas as any).__r3f.invalidate();
              } catch (e) {
                // Ignore errors
              }
            }
          });
        }, 100);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleLogin = () => {
    navigate('/login');
    onClose();
  };

  const handleSignup = () => {
    navigate('/login');
    onClose();
  };

  return (
    <div className="simple-modal-overlay" onClick={onClose}>
      <div className="simple-modal" onClick={(e) => e.stopPropagation()}>
        <div className="simple-modal-header">
          <h2>{t('anonymousLogin.title')}</h2>
          <button 
            className="simple-close-btn" 
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        
        <div className="simple-modal-content">
          <p>
            {downloadType === 'gcode' 
              ? t('anonymousLogin.downloadGcode.description')
              : t('anonymousLogin.downloadFile.description')
            }
          </p>
          
          <div className="simple-benefits">
            <h4>{t('anonymousLogin.benefits.title')}</h4>
            <ul>
              <li>
                <img src="/checkmark_icon.svg" alt="✓" className="checkmark-icon" />
                <span>{t('anonymousLogin.benefits.saveProjects')}</span>
              </li>
              <li>
                <img src="/checkmark_icon.svg" alt="✓" className="checkmark-icon" />
                <span>{t('anonymousLogin.benefits.downloadAnytime')}</span>
              </li>
              <li>
                <img src="/checkmark_icon.svg" alt="✓" className="checkmark-icon" />
                <span>{t('anonymousLogin.benefits.trackHistory')}</span>
              </li>
              <li>
                <img src="/checkmark_icon.svg" alt="✓" className="checkmark-icon" />
                <span>{t('anonymousLogin.benefits.freeStorage')}</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="simple-modal-actions">
          <button 
            className="simple-primary-btn"
            onClick={handleSignup}
          >
            {t('anonymousLogin.actions.createAccount')}
          </button>
          <button 
            className="simple-secondary-btn"
            onClick={handleLogin}
          >
            {t('anonymousLogin.actions.signIn')}
          </button>
        </div>
      </div>
    </div>
  );
} 